import React, { useCallback } from "react";
import { message, Tooltip, Flex } from "antd";
import {
  LikeFilled,
  LikeOutlined,
  ReloadOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import copy from "copy-to-clipboard";
import XAdkChatbot from "../../XAdkChatbot";
import DislikeFeedback from "./DislikeFeedBack";
import { useChatState } from "../context/ChatStateContext";
import { useChatActions } from "../context/ChatActionContext";
import type { ActionExtraProps, ActionProps } from "@/types";
import type { ChatStrategies } from "@/types";

export interface XAdkMessagesProps {
  /** 自定义操作栏渲染 */
  actions?: ActionProps;
  /** 追加在操作栏之后的内容 */
  actionsExtra?: ActionExtraProps;
  /** 类名 */
  className?: string;
  /** 样式 */
  style?: React.CSSProperties;
  /** 是否显示函数调用详情 */
  showFnCallDetail?: boolean;
  /** 自定义工具调用渲染函数，返回 null 则降级使用默认渲染 */
  renderFunctionCall?: (msg: import("@/types").IMessage) => React.ReactNode | null;
  /** 局部策略覆盖，优先级高于 Provider preset/strategies */
  strategies?: ChatStrategies;
}

/**
 * Messages - XAdkProvider 的消息展示组件
 *
 * 使用 XAdkChatbot 渲染消息列表
 */
const Messages: React.FC<XAdkMessagesProps> = ({
  actions,
  actionsExtra,
  className,
  style,
  showFnCallDetail,
  renderFunctionCall,
  strategies: strategiesProp,
}) => {
  const {
    messages,
    loading,
    prologue,
    suggestions,
    initialized,
    showFeedback,
    appInfo,
    onFileClick,
    strategies,
  } = useChatState();
  const { suggestChat, reChat, likeMessage, confirmFnCall } = useChatActions();

  const primaryColor = appInfo?.appTheme || "#1677ff";

  const defaultActions = useCallback<ActionProps>(
    ({ message: group, isLastBotMsg }) => {
      // 取该组最后一条 bot 消息的 invocationId 和 isLike 状态
      const lastMsg = group.msgs[group.msgs.length - 1];
      const invocationId = lastMsg?.invocationId;
      const isLikeVal = group.isLike ?? 0;

      const fullText = group.msgs
        .filter((m) => !m.functionCall && !m.functionResponse)
        .map((m) => m.text || "")
        .join("");

      return (
        <Flex
          gap={16}
          align="center"
          style={{ padding: "4px 0", color: "#81858c" }}
        >
          {isLastBotMsg && (
            <Tooltip title="重新生成">
              <ReloadOutlined style={{ cursor: "pointer" }} onClick={reChat} />
            </Tooltip>
          )}
          <Tooltip title="复制内容">
            <CopyOutlined
              style={{ cursor: "pointer" }}
              onClick={() => {
                copy(fullText);
                message.success("复制成功");
              }}
            />
          </Tooltip>
          {showFeedback && invocationId && (
            <>
              <Tooltip title="赞">
                {isLikeVal === 1 ? (
                  <LikeFilled
                    style={{ color: primaryColor, cursor: "pointer" }}
                  />
                ) : (
                  <LikeOutlined
                    style={{ cursor: "pointer" }}
                    onClick={async () => {
                      if (isLikeVal !== 0) {
                        message.warning("已收到您的反馈，无需重复提交");
                        return;
                      }
                      const ok = await likeMessage(invocationId, 1);
                      if (ok) message.success("感谢您的反馈");
                      else message.error("反馈失败，请稍后再试");
                    }}
                  />
                )}
              </Tooltip>
              <DislikeFeedback
                msg={group}
                activeColor={primaryColor}
                onSubmit={async (formData) => {
                  const ok = await likeMessage(invocationId, -1, formData);
                  if (ok) message.success("已收到您的反馈，我们会努力做得更好");
                  else message.error("反馈失败，请稍后再试");
                  return ok;
                }}
              />
            </>
          )}
        </Flex>
      );
    },
    [reChat, likeMessage, showFeedback, primaryColor],
  );

  return (
    <XAdkChatbot
      messages={messages}
      loading={loading}
      prologue={prologue}
      suggestions={suggestions}
      onSuggest={suggestChat}
      onConfirm={confirmFnCall}
      // 操作栏：使用外部传入的 actions，否则用内置默认 actions
      actions={actions ?? defaultActions}
      actionsExtra={actionsExtra}
      // 启用分组和解析
      enableGrouping={true}
      enableProcessParsing={true}
      initialized={initialized}
      showFnCallDetail={showFnCallDetail}
      // 文件点击回调
      onFileClick={onFileClick}
      // 样式
      className={className}
      style={style}
      // 自定义工具调用渲染
      renderFunctionCall={renderFunctionCall}
      strategies={{ ...strategies, ...strategiesProp }}
    />
  );
};

export default Messages;
